import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarSearch } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectPortalBookableSlots } from "../../features/bookable-slots/bookable-slots-slice";
import { BookableSlot } from "../../features/bookable-slots/models/BookableSlots";
import { isSlotTaken } from "../../features/bookable-slots/utils/slotApiError";
import { selectPortalProgress } from "../../features/portal/portal-slice";
import {
  fetchPortalBookableSlots,
  requestPortalSlot,
} from "../../features/portal/slice-extensions/portal-slots-slice-extension";
import { fetchPortalOccurrences } from "../../features/portal/slice-extensions/portal-slice-extension";
import { usePortalVocabulary } from "../../features/portal/hooks/usePortalVocabulary";
import { SeatStatus } from "../../features/sessions/enums/SeatStatus";
import SlotDayList from "../bookable-slots/SlotDayList";

interface PortalSlotRequestCardProps {
  memberId: string;
  /** The portal schedule's window, refreshed after a request so the new date shows there too. */
  scheduleRange: { from: string; to: string };
}

type Outcome = { kind: "booked" | "requested" | "taken" | "failed"; message?: string };

/**
 * "Request a slot": the free slots of the member's assigned instructor for two weeks.
 * Pick a time (and, for a learner with a programme, a practical topic), then Request.
 * Hidden when the instructor publishes no slots; says so when no instructor is assigned,
 * but only when the organization publishes slots at all (otherwise the line would promise
 * a feature the school doesn't use).
 */
const PortalSlotRequestCard = ({ memberId, scheduleRange }: PortalSlotRequestCardProps) => {
  const { t } = useTranslation();
  const vocabulary = usePortalVocabulary();
  const dispatch = useAppDispatch();
  const slots = useAppSelector(selectPortalBookableSlots(memberId));
  const progress = useAppSelector(selectPortalProgress(memberId));
  const [selected, setSelected] = useState<BookableSlot | null>(null);
  const [topicId, setTopicId] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    setSelected(null);
    setOutcome(null);
    dispatch(fetchPortalBookableSlots({ memberId }));
  }, [dispatch, memberId]);

  // Practical topics of the learner's programmes, once each.
  const topics = useMemo(() => {
    const seen = new Map<string, string>();
    for (const enrolment of progress ?? []) {
      for (const topic of enrolment.topics) {
        if (topic.kind === "Practical" && !seen.has(topic.topicId)) {
          seen.set(topic.topicId, `${topic.code} · ${topic.title}`);
        }
      }
    }
    return [...seen.entries()];
  }, [progress]);

  const handleRequest = async () => {
    if (!selected) return;
    setBusy(true);
    setOutcome(null);
    const result = await dispatch(
      requestPortalSlot({
        memberId,
        request: {
          date: selected.date,
          timeLocal: selected.timeLocal,
          unitMinutes: selected.unitMinutes,
          topicId: topicId || null,
        },
      }),
    );
    setBusy(false);
    setSelected(null);
    if (requestPortalSlot.fulfilled.match(result)) {
      const booked = result.payload.response.status === SeatStatus.Confirmed;
      setOutcome({ kind: booked ? "booked" : "requested" });
      dispatch(fetchPortalOccurrences({ memberId, from: scheduleRange.from, to: scheduleRange.to }));
    } else {
      setOutcome(isSlotTaken(result.payload) ? { kind: "taken" } : { kind: "failed", message: result.payload?.message });
    }
    dispatch(fetchPortalBookableSlots({ memberId }));
  };

  if (!slots) return null;
  if (!slots.hasInstructor && !slots.organizationPublishesSlots) return null;
  if (slots.hasInstructor && slots.days.length === 0 && !outcome) return null;

  return (
    <div className="with-border mb-8 rounded-2xl bg-white p-4 dark:bg-primary-10">
      <h2 className="flex items-center gap-2 text-base font-semibold text-grey-20 dark:text-white">
        <CalendarSearch size={20} />
        {t("portal.slots.title")}
      </h2>
      {!slots.hasInstructor ? (
        <p className="mt-2 text-sm text-grey-40 dark:text-grey-80">{t("portal.slots.noInstructor", vocabulary)}</p>
      ) : (
        <>
          {slots.instructorName && (
            <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
              {t("portal.slots.instructorLine", { ...vocabulary, name: slots.instructorName })}
            </p>
          )}
          <OutcomeNote outcome={outcome} />
          <div className="mt-3">
            <SlotDayList days={slots.days} selectedKey={selected?.key ?? null} onSelect={setSelected} disabled={busy} />
          </div>
          {selected && (
            <div className="mt-4 flex flex-col gap-3">
              {topics.length > 0 && (
                <label className="label">
                  <span className="label-text">{t("portal.slots.topic")}</span>
                  <select className="input w-full sm:w-80" value={topicId} onChange={(e) => setTopicId(e.target.value)}>
                    <option value="">{selected.defaultTopicTitle ?? t("portal.slots.anyTopic")}</option>
                    {topics.map(([id, label]) => (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {selected.needsConfirmation && (
                <p className="text-xs text-grey-40 dark:text-grey-80">{t("portal.booking.needsConfirmation", vocabulary)}</p>
              )}
              <div>
                <button type="button" className="btn btn-primary" onClick={handleRequest} disabled={busy}>
                  <CalendarSearch size={18} />
                  {busy ? t("common.loading", "Loading…") : t("portal.slots.request")}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const OutcomeNote = ({ outcome }: { outcome: Outcome | null }) => {
  const { t } = useTranslation();
  if (!outcome) return null;
  if (outcome.kind === "booked" || outcome.kind === "requested") {
    return (
      <p role="status" className="mt-3 rounded-2xl bg-green-100 p-3 text-sm text-green-800 dark:bg-green-900/30 dark:text-green-300">
        {t(outcome.kind === "booked" ? "portal.slots.confirmed" : "portal.slots.requested")}
      </p>
    );
  }
  return (
    <p role="alert" className="field-error mt-3">
      {outcome.kind === "taken" ? t("portal.slots.taken") : outcome.message || t("portal.slots.failed")}
    </p>
  );
};

export default PortalSlotRequestCard;
